import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MapPin, Phone, Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n";
import { useMeta } from "@/lib/useMeta";
import { api } from "@/lib/api";
import { PublicPage, SocialLinks, ADDRESS, MAP_QUERY, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/components/site";

const EMPTY = { name: "", phone: "", email: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(f) {
  const e = {};
  if (f.name.trim().length < 2) e.name = "errName";
  const digits = f.phone.replace(/\D/g, "");
  if (!/^\+?[0-9 ()-]{10,20}$/.test(f.phone.trim()) || digits.length < 10 || digits.length > 15) e.phone = "errPhone";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "errEmail";
  if (f.message.trim().length < 10) e.message = "errMessage";
  return e;
}

function FieldRow({ id, label, error, children }) {
  const { t } = useI18n();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p id={`${id}-error`} className="text-xs text-red-400" data-testid={`contact-${id}-error`}>{t(`contactPage.${error}`)}</p>}
    </div>
  );
}

function ContactForm() {
  const { t, errMsg } = useI18n();
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    setServerError("");
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      await api.post("/contact", { ...f, name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), message: f.message.trim() });
      setSent(true);
      setF(EMPTY);
    } catch (err) { setServerError(errMsg(err)); } finally { setBusy(false); }
  };
  if (sent) return (
    <div className="panel p-8 text-center flex flex-col items-center gap-3" role="status" data-testid="contact-success">
      <CheckCircle2 className="text-emerald-400" size={40} />
      <p className="text-lg font-semibold">{t("contactPage.successTitle")}</p>
      <p className="text-sm text-neutral-400 max-w-sm">{t("contactPage.success")}</p>
      <button onClick={() => setSent(false)} className="mt-2 text-sm text-emerald-400 hover:underline" data-testid="contact-send-another">{t("contactPage.sendAnother")}</button>
    </div>
  );
  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={set(k)} aria-invalid={!!errors[k]} aria-describedby={errors[k] ? `${k}-error` : undefined}
      className={errors[k] ? "border-red-500" : ""} data-testid={`contact-${k}-input`} {...props} />
  );
  return (
    <form onSubmit={submit} noValidate className="panel p-6 sm:p-8 space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <FieldRow id="name" label={t("contactPage.name")} error={errors.name}>{inp("name", { autoComplete: "name", maxLength: 100 })}</FieldRow>
        <FieldRow id="phone" label={t("contactPage.phone")} error={errors.phone}>{inp("phone", { type: "tel", autoComplete: "tel", inputMode: "tel", maxLength: 20, placeholder: "0542 225 44 67" })}</FieldRow>
      </div>
      <FieldRow id="email" label={t("contactPage.email")} error={errors.email}>{inp("email", { type: "email", autoComplete: "email", maxLength: 254 })}</FieldRow>
      <FieldRow id="message" label={t("contactPage.message")} error={errors.message}>
        <Textarea id="message" rows={6} value={f.message} onChange={set("message")} maxLength={5000} aria-invalid={!!errors.message}
          className={errors.message ? "border-red-500" : ""} data-testid="contact-message-input" />
      </FieldRow>
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      {serverError && <p role="alert" className="text-sm text-red-400" data-testid="contact-server-error">{serverError}</p>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-xs text-neutral-500 max-w-sm">{t("contactPage.privacyNote")} <Link to="/legal/privacy" className="text-emerald-400 hover:underline">{t("legal.privacy")}</Link></p>
        <button disabled={busy} className="btn-brand rounded-full px-7 py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{busy ? t("contactPage.sending") : t("contactPage.send")}
        </button>
      </div>
    </form>
  );
}

function InfoCard() {
  const { t } = useI18n();
  const email = t("landing.contactEmail");
  const row = "flex gap-3 text-sm";
  return (
    <div className="panel p-6 sm:p-8 space-y-5" data-testid="contact-info">
      <h2 className="text-lg font-semibold">{t("contactPage.infoTitle")}</h2>
      <p className={row}><MapPin size={18} className="text-emerald-500 shrink-0" /><span><span className="block text-neutral-500 text-xs">{t("contactPage.addressLabel")}</span><span data-testid="contact-address">{ADDRESS}</span></span></p>
      <a href={`tel:${PHONE_TEL}`} className={`${row} hover:text-emerald-400`} data-testid="contact-phone-link"><Phone size={18} className="text-emerald-500 shrink-0" />{PHONE_DISPLAY}</a>
      <a href={`mailto:${email}`} className={`${row} hover:text-emerald-400 break-all`} data-testid="contact-email-link"><Mail size={18} className="text-emerald-500 shrink-0" />{email}</a>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-full px-5 py-2.5 text-sm font-semibold bg-[#25D366] text-white hover:opacity-90" data-testid="contact-whatsapp-btn">{t("site.whatsapp")}</a>
      <div>
        <p className="text-xs text-neutral-500 mb-2">{t("site.follow")}</p>
        <SocialLinks testid="contact-social" />
      </div>
    </div>
  );
}

export default function Contact() {
  const { t, lang } = useI18n();
  useMeta(t("meta.contact.title"), t("meta.contact.desc"), { page: "contact" });
  const q = encodeURIComponent(MAP_QUERY);
  return (
    <PublicPage testid="contact-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="eyebrow mb-4">{t("nav.contact")}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold" data-testid="contact-title">{t("contactPage.title")}</h1>
        <p className="mt-4 text-neutral-400 text-base md:text-lg max-w-2xl">{t("contactPage.sub")}</p>
        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7"><ContactForm /></div>
          <div className="lg:col-span-5"><InfoCard /></div>
        </div>
        <div className="mt-6 panel overflow-hidden" data-testid="contact-map">
          <iframe title={t("contactPage.mapTitle")} src={`https://www.google.com/maps?q=${q}&hl=${lang}&output=embed`} loading="lazy"
            referrerPolicy="no-referrer-when-downgrade" className="w-full h-[320px] sm:h-[400px] border-0 block" allowFullScreen />
          <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer"
            className="block px-5 py-3 text-sm text-emerald-400 hover:underline" data-testid="contact-map-link">{t("contactPage.openMap")}</a>
        </div>
      </div>
    </PublicPage>
  );
}
